import type {
  CSSProperties,
  ElementType,
  ReactElement,
  ReactNode,
  RefObject,
} from 'react';
import {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import type { TransitionStatus } from 'react-transition-group';
import { Transition } from 'react-transition-group';

import classNames from 'classnames';

import styles from './AnimateHeight.module.css';

import { Animation } from '../../styles';

export interface AnimateHeightProps {
  /**
   * The component to wrap the children with. Set it to `null` to disable the
   * wrapper and use the children as animated element.
   *
   * @defaultValue div
   */
  component?: ElementType | null;
  /** A ref to the node that will be animated. */
  nodeRef: RefObject<HTMLElement>;
  /**
   * Toggle the animation.
   *
   * @defaultValue false
   */
  animateIn?: boolean;
  children?: ReactNode;
}

/**
 * Animates the height and the opacity of the children when the `animateIn` prop
 * changes.
 *
 * @see https://corgi-x.tfd.engineering/components/animateheight
 */
export function AnimateHeight(props: AnimateHeightProps) {
  const { animateIn, nodeRef, component: Component = 'div', children } = props;
  const [height, setHeight] = useState(animateIn ? undefined : 0);

  const calculateHeight = useCallback(() => {
    if (!nodeRef?.current) {
      return;
    }
    setHeight(nodeRef.current?.scrollHeight || height);
  }, [height, nodeRef]);

  // Recalculate the max height when the props changes
  useEffect(
    () => calculateHeight(),
    [animateIn, nodeRef, height, calculateHeight]
  );

  useEffect(() => {
    if (!nodeRef?.current) {
      return;
    }

    const observer = new ResizeObserver(() => {
      calculateHeight();
    });

    observer.observe(nodeRef.current);

    return () => {
      observer.disconnect();
    };
  }, [nodeRef, calculateHeight]);

  // Recalculate the max height when the viewport or the orientation change
  useEffect(() => {
    if (animateIn) {
      window.addEventListener('resize', calculateHeight);
      window.addEventListener('orientationchange', calculateHeight);
    } else {
      window.removeEventListener('resize', calculateHeight);
      window.removeEventListener('orientationchange', calculateHeight);
    }
    return () => {
      window.removeEventListener('resize', calculateHeight);
      window.removeEventListener('orientationchange', calculateHeight);
    };
  }, [calculateHeight, animateIn]);

  const transitions: Record<TransitionStatus, CSSProperties> = {
    entering: { opacity: 1, height, overflow: 'hidden' },
    entered: {
      opacity: animateIn ? 1 : 0,
      height: animateIn ? height : 0,
      pointerEvents: animateIn ? undefined : 'none',
    },
    exiting: { opacity: 0, height: 0, overflow: 'hidden' },
    exited: {
      opacity: animateIn ? 1 : 0,
      height: animateIn ? height : 0,
      pointerEvents: animateIn ? undefined : 'none',
    },
    unmounted: {},
  };

  return (
    <Transition
      in={animateIn}
      component={null}
      timeout={Animation.StandardDuration}
      nodeRef={nodeRef}
      appear
      onExited={calculateHeight}
    >
      {status => {
        if (Component !== null) {
          return (
            <Component
              className={styles.heightTransition}
              style={transitions[status]}
            >
              {children}
            </Component>
          );
        }
        const child = Children.only(children);
        if (!isValidElement<HTMLElement>(child)) {
          return child;
        }
        const className = classNames(
          styles.heightTransition,
          child.props.className
        );
        const style = { ...child.props.style, ...transitions[status] };
        return cloneElement(child as ReactElement, {
          className,
          style,
          ref: nodeRef,
        });
      }}
    </Transition>
  );
}
